import {
  getDiscoveryTargetForOptionId,
  getDiscoveryTargetForPath,
} from '@revelio/core/global-ai-assistant/discovery-targets';
import {
  getPageCatalogEntry,
  PAGE_NAVIGATE_PATH_OVERRIDES,
} from '@revelio/core/global-ai-assistant/pages';
import type { Tab } from '@revelio/data-access';

import { getGlobalAssistantRouteAccess } from './global-ai-assistant-access';
import type { GlobalAssistantSuggestMatch } from './global-ai-assistant-api';
import type { GlobalAiSingleChartTranscriptMessage } from './global-ai-assistant-transcript';
import type { NavigateToOptionResult } from './global-ai-client-tool-deps';

/**
 * The catalog already stores the finished display name, so the label is a
 * lookup on the page's option id rather than a reconstruction from per-segment
 * label maps. A path the catalog does not describe falls back to the generic
 * 'standard page' wording.
 */
export function toStandardPageLabel(standardPagePath: string): string {
  return (
    getPageCatalogEntry(toStandardPageId(standardPagePath))?.pageName ??
    'standard page'
  );
}

/** Route → option id for the pages the catalog gives an explicit route. */
const OPTION_ID_BY_OVERRIDDEN_PATH: ReadonlyMap<string, string> = new Map(
  [...PAGE_NAVIGATE_PATH_OVERRIDES].map(([optionId, path]) => [path, optionId])
);

/**
 * The `__std__:` id for the page the user is currently ON, or undefined when the
 * catalog describes no enabled page for that route (a custom dash, the home
 * screen, an app route with no assistant page). This is the INVERSE of
 * `getStandardPageMatchForOptionId`, and it exists so the loop can tell that the
 * page it is about to offer is the page already on screen — the id space is the
 * same one `navigate.optionId` / `offer_page_or_custom.optionId` /
 * `offer_report.pageOptionId` draw from, so the comparison is a string equality
 * rather than a path-shape guess.
 */
export function getCurrentPageOptionId(pathname: string): string | undefined {
  const optionId = toStandardPageId(pathname);
  return getPageCatalogEntry(optionId) ? optionId : undefined;
}

function toStandardPageId(standardPagePath: string): string {
  const segments = standardPagePath.split('/').filter(Boolean);
  const firstSegment = segments[0];
  const secondSegment = segments[1];
  if (!firstSegment) {
    return '__std__:custom:standard-page';
  }
  // A page whose route the id cannot reconstruct (Data Enrichment lives under
  // the Deliverables parent) has to be recognised by route here too, or the
  // offer button labels it "standard page".
  const overriddenOptionId = OPTION_ID_BY_OVERRIDDEN_PATH.get(
    `/${segments.join('/')}`
  );
  if (overriddenOptionId) {
    return overriddenOptionId;
  }
  // Every single-segment route is an `app`-entity page (Individual Discovery,
  // Posting Discovery, Data Dictionary). Mapping the segment generically rather
  // than naming one of them keeps this in step with the catalog: an id the
  // catalog does not describe simply finds no entry and falls back.
  if (!secondSegment) {
    return `__std__:app:${firstSegment}`;
  }
  return `__std__:${firstSegment}:${secondSegment}`;
}

/**
 * Invert a `__std__:{entity}:{pathSegment}` candidate id (the turn loop's
 * `navigate.optionId`, produced by the backend canonical page intents) into a
 * navigable suggest match. `app`-entity ids map to a top-level route
 * (`/${pathSegment}`); entity ids map to `/${entity}/${pathSegment}`. Returns
 * null for an id that doesn't parse as a standard option, or that the page
 * catalog does not describe an enabled page for.
 */
export function getStandardPageMatchForOptionId(
  optionId: string,
  options?: { resolvedPromptForApply?: string }
): GlobalAssistantSuggestMatch | null {
  const match = optionId.trim().match(/^__std__:([a-z]+):([a-z0-9-]+)$/i);
  if (!match) {
    return null;
  }
  const entity = match[1]?.toLowerCase();
  const segment = match[2]?.toLowerCase();
  if (!entity || !segment) {
    return null;
  }
  // Catalog lookup validates the PAIR, not each half independently — Market
  // Conditions has no company page, so `__std__:company:market-conditions` must
  // not resolve even though both halves are individually valid.
  const entry = getPageCatalogEntry(`__std__:${entity}:${segment}`);
  if (!entry) {
    return null;
  }
  const navigatePath =
    PAGE_NAVIGATE_PATH_OVERRIDES.get(entry.optionId) ??
    (entity === 'app' ? `/${segment}` : `/${entity}/${segment}`);
  // Both app-level Discovery surfaces resolve their filters and apply them on
  // landing. Posting Discovery's filters are a subset of Individual Discovery's,
  // so it shares the same tdV2 resolver + applier; only the `discoveryKind`
  // differs (it selects the destination, not the parser).
  const discovery = getDiscoveryTargetForOptionId(entry.optionId);
  // Preserve the existing app-only resolve-first branch; screeners keep their
  // standard-page navigation/continuation behavior.
  const discoveryKind = entity === 'app' ? discovery?.discoveryKind : undefined;
  return {
    pageId: optionId,
    name: entry.pageName,
    navigatePath,
    confidenceScore: 100,
    ...(discoveryKind
      ? { discoveryKind, applyFiltersAfterNavigate: true }
      : {}),
    ...(options?.resolvedPromptForApply
      ? { resolvedPromptForApply: options.resolvedPromptForApply }
      : {}),
  };
}

/**
 * Resolve a loop `navigate` candidate id into a discriminated outcome,
 * enforcing the same route-access/entitlement gate the manual chip uses.
 * The caller supplies `performNavigate`, which actually drives the
 * router (via `navigateToMatch`) and reports whether navigation proceeded —
 * the unsaved-changes redirect guard can cancel it. Returns:
 *  - `{ navigatedTo }` on success
 *  - `notNavigated` with reason `unknown_page` (id didn't map to a page),
 *    `no_access` (+ featureLabel/message, entitlement denied), or `cancelled`
 *    (redirect guard cancelled).
 * Keeping this pure makes the access/cancel/unknown branches unit-testable
 * without a full component render.
 */
export async function resolveNavigateToOptionId(
  optionId: string,
  userTabs: Tab[],
  // Async since navigateToMatch became resolve-first (awaits filter
  // resolution before navigating); a sync performNavigate still works —
  // its plain return value is awaited transparently.
  performNavigate: (
    match: GlobalAssistantSuggestMatch
  ) => Promise<{ navigated: boolean }> | { navigated: boolean }
): Promise<NavigateToOptionResult> {
  const match = getStandardPageMatchForOptionId(optionId, {});
  if (!match) {
    return { notNavigated: true, reason: 'unknown_page' };
  }
  const access = getGlobalAssistantRouteAccess(match.navigatePath, userTabs);
  if (!access.allowed) {
    return {
      notNavigated: true,
      reason: 'no_access',
      featureLabel: access.featureLabel,
      message: access.message,
    };
  }
  const { navigated } = await performNavigate(match);
  if (!navigated) {
    return { notNavigated: true, reason: 'cancelled' };
  }
  return { navigatedTo: match.name };
}

export function getSingleChartStandardPageMatch(
  msg: GlobalAiSingleChartTranscriptMessage
): GlobalAssistantSuggestMatch | null {
  const standardPagePath = msg.standardPagePath?.trim();
  if (!standardPagePath) {
    return null;
  }
  const isIndividualDiscovery =
    getDiscoveryTargetForPath(standardPagePath)?.discoveryKind === 'talent';
  const maybeResolvedPrompt = msg.prompt?.trim();
  return {
    pageId: toStandardPageId(standardPagePath),
    name: toStandardPageLabel(standardPagePath),
    navigatePath: standardPagePath,
    confidenceScore: 100,
    ...(isIndividualDiscovery
      ? {
          discoveryKind: 'talent' as const,
          applyFiltersAfterNavigate: true,
        }
      : {}),
    ...(maybeResolvedPrompt
      ? { resolvedPromptForApply: maybeResolvedPrompt }
      : {}),
  };
}
